import React, { useState, useEffect } from "react";
import axios from "axios";

const PeopleArchive = () => {
  const [locations, setLocations] = useState([]);
  const [services, setServices] = useState([]);
  const [specialisms, setSpecialisms] = useState([]);
  const [people, setPeople] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedSpecialism, setSelectedSpecialism] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [perPage] = useState(16);
  const [maxPage, setMaxPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [initialLoading, setInitialLoading] = useState(true);


  const paginationParam = `&per_page=${perPage}&page=${page}`;

  const updatePeople = async (append = false, page = 1) => {
    if (!append) {
      setInitialLoading(true);
    }
    setLoading(true);
    let searchString = "";
    if (selectedLocation) {
      searchString += `&location=${selectedLocation.id}`;
    }
    if (selectedService) {
      searchString += `&service=${selectedService.id}`;
    }
    if (selectedSpecialism) {
      searchString += `&specialism-type=${selectedSpecialism.id}`;
    }
    if (search) {
      searchString += `&search=${search}`;
    }

    const paginationParam = `&per_page=${perPage}&page=${page}`;

    try {
      const results = await axios.get(
        `/wp-json/gs/v1/get_people?${searchString}${paginationParam}`
      );
      setMaxPage(+results.headers["x-wp-totalpages"]);
      if (append) {
        setPeople((prevPeople) => [...prevPeople, ...results.data]);
      } else {
        setPeople(results.data);
      }
    } catch (error) {
      console.error("Error fetching people:", error);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const loadNextPage = async () => {
    await updatePeople(true, page + 1);
    setPage((prevPage) => prevPage + 1);
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setInitialLoading(true);
        const [locationsData, servicesData, specialismsData] = await Promise.all([
          axios.get('/wp-json/wp/v2/office?per_page=100'),
          axios.get('/wp-json/wp/v2/service?per_page=100'),
          axios.get('/wp-json/wp/v2/specialism-type?per_page=100')
        ]);
  
        setLocations(locationsData.data);
        setServices(servicesData.data);
        setSpecialisms(specialismsData.data);
        
        // Fetch people after taxonomy data is loaded
        await updatePeople();
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };
  
    fetchInitialData();
  }, []); // Empty dependency array for initial load only
  
  // Separate effect for filter changes
  useEffect(() => {
    if (locations.length > 0) { // Only update if initial data is loaded
      updatePeople();
    }
  }, [selectedLocation, selectedService, selectedSpecialism]);
  
  const handleSearchKeyUp = (event) => {
    if (event.key === "Enter") {
      updatePeople();
    }
  }; 

  const handleSelectLocation = (event) => {
    const id = +event.target.value;
    setSelectedLocation(locations.find((x) => x.id === id) || null);
  };

  const handleSelectService = (event) => {
    const id = +event.target.value;
    setSelectedService(services.find((x) => x.id === id) || null);
  };

  const handleSelectSpecialism = (event) => {
    const id = +event.target.value;
    setSelectedSpecialism(specialisms.find((x) => x.id === id) || null);
  };

  return (
    <div className="peoplearchive">
      <div className="peoplearchive__searchbar">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 offset-lg-4">
              <div className="input-group mb-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search G+S Staff..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyUp={handleSearchKeyUp}
                />
                <div className="input-group-append">
                  <button
                    className="btn peoplearchive__searchbutton"
                    type="button"
                    onClick={() => updatePeople()}
                  >
                    <i className="fas fa-search"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="peoplearchive__people">
        <div className="container">
          <div className="row">
            <div className="col-md-4 peoplearchive__search">
              <h3>Filter</h3>
              <div className="row">
                <div className="col-12 col-sm-4 col-md-12">
                  <select onChange={handleSelectLocation} defaultValue="">
                    <option value="">Location</option>
                    {locations.map((location, index) => (
                      <option key={`location-${index}`} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-sm-4 col-md-12">
                  <select onChange={handleSelectService} defaultValue="">
                    <option value="">Service</option>
                    {services.map((service, index) => (
                      <option key={`service-${index}`} value={service.id}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-sm-4 col-md-12">
                  <select onChange={handleSelectSpecialism} defaultValue="">
                    <option value="">Specialism</option>
                    {specialisms.map((specialism, index) => (
                      <option key={`specialism-${index}`} value={specialism.id}>
                        {specialism.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="col-md-8">
              <div className="row peoplearchive__results">
                {initialLoading ? (
                  Array.from({ length: perPage }).map((_, index) => (
                    <div
                      className="peoplearchive__personcard col-md-3 col-4 skeleton"
                      key={`skeleton-${index}`}
                    >
                      <div className="skeleton__photo"></div>
                      <div className="skeleton__name"></div>
                      <div className="skeleton__phone"></div>
                      <div className="skeleton__location"></div>
                    </div>
                  ))
                ) : people.length > 0 ? (
                  people.map((person, index) => (
                    <div
                      className="peoplearchive__personcard col-md-3 col-4"
                      key={`person-${index}`}
                    >
                      <a href={person.url}>
                        <img
                          className="peoplearchive__photo"
                          src={person.image}
                          alt={person.name}
                        />
                      </a>
                      <div
                        className="peoplearchive__name"
                        dangerouslySetInnerHTML={{ __html: person.name }}
                      ></div>
                      <div
                        className="peoplearchive__phone"
                        dangerouslySetInnerHTML={{ __html: person.phone }}
                      ></div>
                      <div
                        className="peoplearchive__location"
                        dangerouslySetInnerHTML={{ __html: person.location }}
                      ></div>
                      <a href={person.url}>Get in touch</a>
                    </div>
                  ))
                ) : (
                  <p className="noresults">There are no results for this search</p>
                )}
              </div>
              {page < maxPage  && (
                <>
                  <div className="row peopleviewmore">
                    <div className="col-md-12">
                      <button
                        className="cta mx-auto"
                        onClick={() => {if (!loading) {loadNextPage()}}}
                      >
                        {loading? "Loading..." : "View More"}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PeopleArchive;
